// Context/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { api } from '../api';

// Create the AuthContext
export const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const idleTimeoutRef = useRef(null);
  const tokenRef = useRef(null);

  const idleLogoutTimeout = 5 * 60 * 1000; // 5 minutes

  const resetIdleTimer = () => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    idleTimeoutRef.current = setTimeout(() => {
      console.log('User is idle, logging out');
      logout();
    }, idleLogoutTimeout);
  };

  // Set up idle timer event listeners
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];

    const handleActivity = () => {
      resetIdleTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, []);

  // Initialize auth state on app load
  // In your AuthContext initialization
useEffect(() => {
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');
  const storedGoogleUser = localStorage.getItem('googleUser');
  
  tokenRef.current = token;

  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
      }
    }

    if (storedGoogleUser) {
      try {
        const googleUserData = JSON.parse(storedGoogleUser);
        setGoogleUserInfo(googleUserData);
      } catch (error) {
        console.error('Error parsing stored Google user data:', error);
        localStorage.removeItem('googleUser');
      }
    }

    // If no user data but we have a token, fetch user info
    if (!storedUser && token) {
      fetchUser(token);
    } else {
      setIsLoading(false);
    }
  } else {
    setIsLoading(false);
  }
}, []);

  const fetchUser = async (token) => {
    try {
      const response = await api.post('/auth/me');
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (err) {
      console.error('Failed to fetch user', err);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch (error) {
      console.error('Failed to decode token', error);
      return true;
    }
  };

  const login = async (token, userData) => {
    try {
      localStorage.setItem('token', token);
      tokenRef.current = token;
      
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } else {
        await fetchUser(token);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      resetIdleTimer();
    } catch (err) {
      console.error('Login failed', err);
      throw err;
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      setIsLoading(true);
      
      // Decode the JWT token to get user info
      const decoded = jwtDecode(credential);
      
      // Store user information
      const userInfo = {
        id: decoded.sub,
        name: decoded.name,
        email: decoded.email,
        picture: decoded.picture,
        given_name: decoded.given_name,
        family_name: decoded.family_name
      };
      
      setGoogleUserInfo(userInfo);
      localStorage.setItem('googleUser', JSON.stringify(userInfo));
      
     
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove or update the handleGoogleAuth function since it's causing the error
  // The OAuth2Client approach is not compatible with the JWT decode approach you're using

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    } catch (err) {
      console.error('Failed to fetch user', err);
      logout();
      throw err;
    }
  };

  const logout = () => {
    console.log('Logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('googleUser');
    tokenRef.current = null;
    setUser(null);
    setGoogleUserInfo(null);
    delete axios.defaults.headers.common['Authorization'];
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
  };

  const value = {
    user,
    googleUserInfo,
    login,
    logout,
    isLoading,
    loginWithGoogle,
    fetchCurrentUser
    // Remove handleGoogleAuth from the context value since it's causing issues
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};